/**
 * FRU-22743: what a visitor actually got after the Hope boot shell (index.html #hope-boot) was lifted.
 *
 * Sentry has long counted "Hope boot stalled", but a stall is not a failed visit: some stalled visitors go on to answer
 * four Hope steps, others fall back to the original form, cancel to it, or are left looking at an empty host. This records
 * exactly one terminal outcome per boot from what is on screen when the shell goes, plus the stall stages that came first
 * and how long it took. It observes only: loader timeouts, retries, fallback, URL state and layout are unchanged, and no
 * event carries a URL query value, consumer text, form answer, identifier or contact detail.
 */

export type HopeBootOutcome = 'assistant_painted' | 'classic_fallback' | 'cancelled_to_form' | 'unpainted_lift';

export interface HopeBootStallDetail {
  why?: string;
  action?: string;
  timeoutMs?: number;
}

export interface HopeBootOutcomeReport {
  outcome: HopeBootOutcome;
  /** Since navigation start. */
  elapsedMs: number;
  /** elapsedMs less the time the page was hidden (from navigation when the boot shell tracked it, else from when the
   *  recorder started). */
  visibleMs: number;
  /** The stall stages ("why|action") this document went through, in order. Empty for a boot that never stalled. */
  stages: string[];
  stallWhy: string;
  stallAction: string;
  /** This document is the one-shot reload a failed first attempt asked for. */
  retry: boolean;
}

export interface HopeBootOutcomeSinks {
  /** A new stall stage, once per stage. */
  stall(detail: HopeBootStallDetail, stage: string): void;
  /** The terminal outcome. queryable is false for a boot that never stalled and either painted Hope or was cancelled to the
   *  form: those are normal and stay breadcrumbs, so only slow, fallback or unpainted boots become searchable. */
  outcome(report: HopeBootOutcomeReport, queryable: boolean): void;
  /** The page went away while the shell was still up after a stall. reason is 'auto_reload' when the shell's own one-shot
   *  reload took it (the next document reports its own outcome), otherwise 'left_before_outcome'. */
  left(report: Omit<HopeBootOutcomeReport, 'outcome'>, reason: 'auto_reload' | 'left_before_outcome'): void;
}

export interface HopeBootOutcomeEnvironment {
  doc: Document;
  now(): number;
  hidden(): boolean;
  /** The assistant import failure the loader reported, if any (window.__hopeAssistantImportFailed). */
  importFailure(): unknown;
  retry: boolean;
  /** Total time the page has been hidden since navigation, when the boot shell tracked it from the start
   *  (window.__hopeBootHidden). Without it the recorder counts hidden time only from when it was created. */
  hiddenSoFar?: () => number;
}

// The same laid-out Hope content the chat itself waits for before it lifts the shell (ChatComponent.hopeBootPaintedElement).
const PAINTED_HOPE = '#ai-assistant-host app-step.is-current-step, #ai-assistant-host .chat-message, '
  + '#ai-assistant-host app-message, #ai-assistant-host .hope-returning-card';
// The original form only renders while Hope is not open (rates-form: !showAssistant && !assistantLaunchPending).
const ORIGINAL_FORM = '#rates-form #mainForm app-step, #rates-form #mainForm input, #rates-form #mainForm button';

function laidOut(doc: Document, selector: string, minHeight: number): boolean {
  const els = doc.querySelectorAll(selector);
  for (let i = 0; i < els.length; i++) {
    if (els[i].getBoundingClientRect().height > minHeight) { return true; }
  }
  return false;
}

/** What is on screen now that the shell is gone. Removing the shell is not success by itself. */
export function classifyHopeBootOutcome(doc: Document, importFailure: unknown): HopeBootOutcome {
  if (laidOut(doc, PAINTED_HOPE, 20)) { return 'assistant_painted'; }
  if (laidOut(doc, ORIGINAL_FORM, 0)) { return importFailure ? 'classic_fallback' : 'cancelled_to_form'; }
  return 'unpainted_lift';
}

export function hopeBootStallStage(detail: HopeBootStallDetail | null | undefined): string {
  const why = detail && typeof detail.why === 'string' && detail.why ? detail.why : 'timeout';
  const action = detail && typeof detail.action === 'string' && detail.action ? detail.action : 'unknown';
  return why + '|' + action;
}

export class HopeBootOutcomeRecorder {
  private readonly stages: string[] = [];
  private lastStall: HopeBootStallDetail | null = null;
  private settled = false;
  private hiddenMs = 0;
  private hiddenSince: number | null;

  constructor(private readonly env: HopeBootOutcomeEnvironment, private readonly sinks: HopeBootOutcomeSinks) {
    this.hiddenSince = env.hidden() ? env.now() : null;
  }

  /** Records a stall stage once. Returns false for a stage already seen, so a later, different stage is never suppressed. */
  noteStall(detail: HopeBootStallDetail | null | undefined): boolean {
    if (!detail) { return false; }
    const stage = hopeBootStallStage(detail);
    if (this.stages.indexOf(stage) >= 0) { return false; }
    this.stages.push(stage);
    this.lastStall = detail;
    this.sinks.stall(detail, stage);
    return true;
  }

  visibilityChanged(): void {
    if (this.env.hiddenSoFar) { return; }
    const now = this.env.now();
    if (this.env.hidden()) {
      if (this.hiddenSince === null) { this.hiddenSince = now; }
    } else if (this.hiddenSince !== null) {
      this.hiddenMs += now - this.hiddenSince;
      this.hiddenSince = null;
    }
  }

  /** The shell was lifted: classify what the visitor got, once. */
  settle(): HopeBootOutcomeReport | null {
    if (this.settled) { return null; }
    this.settled = true;
    const report: HopeBootOutcomeReport = {
      outcome: classifyHopeBootOutcome(this.env.doc, this.env.importFailure()),
      ...this.timing(),
    };
    const routine = this.stages.length === 0
      && (report.outcome === 'assistant_painted' || report.outcome === 'cancelled_to_form');
    this.sinks.outcome(report, !routine);
    return report;
  }

  /** The page is going away. Only a stalled boot with no outcome yet is worth recording. */
  leave(): void {
    if (this.settled || this.stages.length === 0) { return; }
    this.settled = true;
    const report = this.timing();
    this.sinks.left(report, report.stallAction === 'auto_reload' ? 'auto_reload' : 'left_before_outcome');
  }

  private timing(): Omit<HopeBootOutcomeReport, 'outcome'> {
    const now = this.env.now();
    const hidden = this.env.hiddenSoFar
      ? this.env.hiddenSoFar()
      : this.hiddenMs + (this.hiddenSince !== null ? now - this.hiddenSince : 0);
    return {
      elapsedMs: Math.round(now),
      visibleMs: Math.max(0, Math.round(now - hidden)),
      stages: this.stages.slice(),
      stallWhy: this.lastStall && this.lastStall.why ? String(this.lastStall.why) : '',
      stallAction: this.lastStall && this.lastStall.action ? String(this.lastStall.action) : '',
      retry: this.env.retry,
    };
  }
}

/** FRU-24088: the outcomes that are a failed boot, and the only ones Sentry gets as an error. */
export const HOPE_BOOT_FAILURES: string[] = ['classic_fallback', 'unpainted_lift'];

export interface HopeBootSentry {
  captureMessage(message: string, context: any): unknown;
  addBreadcrumb(breadcrumb: any): unknown;
}

/**
 * The sinks the app uses. Matomo gets every stall stage and one outcome for EVERY settled boot (the denominator), creating
 * its command queue when Matomo has not loaded yet, as AnalyticsService does, so a stall replayed early is not dropped.
 * Sentry, when enabled, gets a "Hope boot outcome" only for a boot that failed: a warning for the classic fallback (the
 * visitor has the classic form), an error for the shell lifting with nothing painted. A slow boot, a departure during a stall and the shell's own reload are timings Matomo
 * already counts per arm, so Sentry keeps them as breadcrumbs (FRU-24088).
 */
export function hopeBootOutcomeSinks(win: Window, sentry: HopeBootSentry | null): HopeBootOutcomeSinks {
  const track = (action: string, name: string): void => {
    try {
      const w = win as any;
      w._paq = w._paq || [];
      w._paq.push(['trackEvent', 'HopeBoot', action, name]);
    } catch (e) { /* telemetry is non-fatal */ }
  };
  const breadcrumb = (data: any): void => {
    if (!sentry) { return; }
    try { sentry.addBreadcrumb({ category: 'hope_boot', message: 'Hope boot outcome', level: 'info', data }); } catch (e) { /* non-fatal */ }
  };
  const capture = (outcome: string, report: Omit<HopeBootOutcomeReport, 'outcome'>): void => {
    if (!sentry) { return; }
    try {
      // FRU-25461 (Sentry ANGULAR-7ED): a classic fallback still gives the visitor the classic form, so it's a warning.
      // An unpainted lift (neither Hope nor the classic form on screen) stays an error.
      sentry.captureMessage('Hope boot outcome', {
        level: outcome === 'classic_fallback' ? 'warning' : 'error',
        tags: {
          feature: 'hope_direct',
          surface: 'hope_boot',
          outcome,
          reason: report.stallWhy || 'none',
          action: report.stallAction || 'none',
          retry: report.retry ? 'yes' : 'no',
        },
        extra: { ...report, outcome },
      });
    } catch (e) { /* telemetry is non-fatal */ }
  };
  return {
    stall: (_detail, stage) => track('stall', stage),
    outcome: (report, queryable) => {
      if (sentry) {
        try {
          if (HOPE_BOOT_FAILURES.indexOf(report.outcome) >= 0) {
            capture(report.outcome, report);
          } else {
            breadcrumb({ ...report, queryable });
          }
        } catch (e) { /* telemetry is non-fatal */ }
      }
      track('outcome', report.outcome);
    },
    // A departure during a stall (or the shell's own reload) keeps its full report (timing, stages, retry) as a breadcrumb.
    left: (report, reason) => { breadcrumb({ ...report, outcome: reason }); track('outcome', reason); },
  };
}

/**
 * Watches a Hope boot on this document. Returns null when there is no Hope boot to watch: an original-form entry (the shell
 * is only switched on for startAssistant=1) or a boot whose shell was already lifted before this ran.
 */
export function watchHopeBoot(win: Window, sinks: HopeBootOutcomeSinks): HopeBootOutcomeRecorder | null {
  const doc = win.document;
  const shell = doc.getElementById('hope-boot');
  if (!shell || !shell.parentNode || !/(?:^|\s)on(?:\s|$)/.test(shell.className)) { return null; }
  const w = win as any;
  const now = () => (win.performance && typeof win.performance.now === 'function' ? win.performance.now() : 0);
  const shellHidden = w.__hopeBootHidden && typeof w.__hopeBootHidden.ms === 'number' ? w.__hopeBootHidden : null;
  const recorder = new HopeBootOutcomeRecorder({
    doc,
    now,
    hidden: () => doc.visibilityState === 'hidden',
    importFailure: () => w.__hopeAssistantImportFailed || null,
    retry: /[?&]hbretry=1(?:&|$)/i.test(win.location.search),
    hiddenSoFar: shellHidden
      ? () => shellHidden.ms + (shellHidden.since !== null && shellHidden.since !== undefined ? now() - shellHidden.since : 0)
      : undefined,
  }, sinks);

  // The shell may have stalled before this bundle ran: replay every stage it recorded, not only the last one.
  const earlier: any[] = Array.isArray(w.__hopeBootStalls) ? w.__hopeBootStalls : (w.__hopeBootStalled ? [w.__hopeBootStalled] : []);
  earlier.forEach((detail) => recorder.noteStall(detail));
  win.addEventListener('hopeBootStalled', (event: Event) => recorder.noteStall((event as CustomEvent).detail));
  doc.addEventListener('visibilitychange', () => recorder.visibilityChanged());
  // A page that goes away after the shell was lifted but before the short settle delay still gets its outcome.
  win.addEventListener('pagehide', () => { if (shell.parentNode) { recorder.leave(); } else { recorder.settle(); } });

  const parent = shell.parentNode;
  const observer = new MutationObserver(() => {
    if (shell.parentNode) { return; }
    observer.disconnect();
    // Let the frame that replaced the shell lay out before reading what is on screen.
    win.setTimeout(() => recorder.settle(), 300);
  });
  observer.observe(parent, { childList: true });
  return recorder;
}
